import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Alert, AlertDescription } from '../components/ui/alert';
import ReactQuill from 'react-quill-new';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import 'react-quill-new/dist/quill.snow.css';
import { AnnotatedContent } from '../components/AnnotatedContent';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  ArrowLeft, FileText, User, Calendar, Send, CheckCircle, 
  XCircle, Clock, AlertCircle, Loader2, History, Mail,
  ChevronRight, ChevronDown, Scale, RefreshCw, UserCheck, Download, Wand2,
  Paperclip, Eye, FileImage, FileVideo, File, X as XIcon, MessageSquare, FileDown, Link2
} from 'lucide-react';

const ESTADOS_RESOLUCION = {
  'EN_PROCESO': { label: 'En Proceso', color: 'bg-blue-100 text-blue-800', icon: Clock },
  'PENDIENTE_APROBACION': { label: 'Pendiente Aprobación', color: 'bg-amber-100 text-amber-800', icon: Clock },
  'APROBADO': { label: 'Aprobado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'POSPUESTO': { label: 'Pospuesto', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const ResolucionPage = () => {
  const { reclamoId } = useParams();
  const navigate = useNavigate();
  const { api, user, hasRole } = useAuth();
  const themeColors = useThemeColors();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [adjuntandoPdf, setAdjuntandoPdf] = useState(false);
  const [aprobando, setAprobando] = useState(false);
  const [reclamo, setReclamo] = useState(null);
  const [resolucion, setResolucion] = useState(null);
  const [contenido, setContenido] = useState('');
  const [historial, setHistorial] = useState([]);

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      [{ 'color': [] }],
      ['link'],
      ['clean']
    ]
  }), []);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [refreshingHistorial, setRefreshingHistorial] = useState(false);
  const historialEndRef = useRef(null);
  
  // Modales
  const [posponerDialogOpen, setPosponerDialogOpen] = useState(false);
  const [motivoPosposicion, setMotivoPosposicion] = useState('');
  const [archivosPosponer, setArchivosPosponer] = useState([]);
  const [enviandoCorreo, setEnviandoCorreo] = useState(false);
  
  // Preview modal
  const [previewModal, setPreviewModal] = useState({ open: false, url: '', tipo: '', nombre: '' });
  
  // Anotaciones inline del aprobador
  const [anotaciones, setAnotaciones] = useState([]);
  
  // IA
  const [generandoIA, setGenerandoIA] = useState(false);
  const [iaHabilitado, setIaHabilitado] = useState(false);
  
  // Modal de selección de aprobador
  const [aprobadorDialogOpen, setAprobadorDialogOpen] = useState(false);
  const [usuariosLegales, setUsuariosLegales] = useState([]);
  const [aprobadorSeleccionado, setAprobadorSeleccionado] = useState('');
  const [aprobadorPopoverOpen, setAprobadorPopoverOpen] = useState(false);
  const [loadingUsuariosLegales, setLoadingUsuariosLegales] = useState(false);

  const esLegal = hasRole('LEGAL');
  const esAdmin = hasRole('ADMINISTRADOR') || hasRole('SUPERADMIN');
  const esAprobadorAsignado = resolucion?.aprobador_asignado_id === user?.id || hasRole('SUPERADMIN');

  useAbortableLoad((signal) => {
    fetchResolucion(signal);
    // Verificar si IA está habilitada
    api.get('/mi-empresa', { signal }).then(res => {
      setIaHabilitado(!!res.data?.ia_resoluciones_habilitado);
    }).catch(() => {});
  }, [reclamoId]);

  // Cargar historial automáticamente cuando existe una resolución
  useAbortableLoad((signal) => {
    if (resolucion) fetchHistorial(false, signal);
  }, [resolucion?.id]);

  const fetchResolucion = async (signal) => {
    setLoading(true);
    try {
      const response = await api.get(`/reclamos/${reclamoId}/resolucion`, { signal });
      setReclamo(response.data.reclamo);
      setResolucion(response.data.resolucion);
      if (response.data.resolucion) {
        setContenido(response.data.resolucion.contenido_resolucion || '');
        // Cargar anotaciones borrador si existen
        if (response.data.resolucion.anotaciones_borrador?.length > 0) {
          setAnotaciones(response.data.resolucion.anotaciones_borrador);
        }
      }
    } catch (err) {
      if (isCanceled(err)) return;
      console.error('Error fetching resolucion:', err);
      toast.error('Error al cargar la resolución');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorial = async (sutil = false, signal) => {
    if (sutil) {
      setRefreshingHistorial(true);
    } else {
      setLoadingHistorial(true);
    }
    try {
      const response = await api.get(`/reclamos/${reclamoId}/resolucion/historial`, { signal });
      setHistorial(response.data.historial || []);
      // Scroll al último registro después de actualizar
      setTimeout(() => {
        historialEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      if (isCanceled(err)) return;
      console.error('Error fetching historial:', err);
    } finally {
      setLoadingHistorial(false);
      setRefreshingHistorial(false);
    }
  };

  // Auto-guardado de anotaciones borrador con debounce (2s)
  const anotacionesInitialLoad = useRef(true);
  useEffect(() => {
    // Skip the initial load (when annotations come from the server)
    if (anotacionesInitialLoad.current) {
      anotacionesInitialLoad.current = false;
      return;
    }
    if (!resolucion?.estado || resolucion.estado !== 'PENDIENTE_APROBACION') return;
    
    const timer = setTimeout(async () => {
      try {
        await api.put(`/reclamos/${reclamoId}/resolucion/anotaciones-borrador`, {
          anotaciones
        });
      } catch (err) {
        console.error('Error auto-saving annotations:', err);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [anotaciones, reclamoId, resolucion?.estado]);

  const handleGuardar = async () => {
    if (!contenido.trim() || contenido === '<p><br></p>') {
      toast.error('El contenido de la resolución no puede estar vacío');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post(`/reclamos/${reclamoId}/resolucion`, {
        contenido_resolucion: contenido
      });
      toast.success(response.data.message);
      fetchResolucion();
      fetchHistorial(true);
    } catch (err) {
      console.error('Error guardando:', err);
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const fetchUsuariosLegales = async () => {
    setLoadingUsuariosLegales(true);
    try {
      const response = await api.get('/usuarios/legales');
      setUsuariosLegales(response.data.usuarios || []);
      if (response.data.usuarios.length === 0) {
        toast.error('No existen usuarios aprobadores disponibles en la empresa.');
        return false;
      }
      return true;
    } catch (err) {
      console.error('Error fetching usuarios legales:', err);
      toast.error('Error al cargar usuarios legales');
      return false;
    } finally {
      setLoadingUsuariosLegales(false);
    }
  };

  const handleAbrirSelectorAprobador = async () => {
    if (!resolucion) {
      toast.error('Primero debe guardar la resolución');
      return;
    }
    
    const hayUsuarios = await fetchUsuariosLegales();
    if (hayUsuarios) {
      setAprobadorSeleccionado('');
      setAprobadorDialogOpen(true);
    }
  };

  const handleEnviarAprobacion = async () => {
    if (!aprobadorSeleccionado) {
      toast.error('Debe seleccionar un aprobador');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/reclamos/${reclamoId}/resolucion/enviar-aprobacion`, {
        aprobador_id: aprobadorSeleccionado
      });
      toast.success('Resolución enviada a aprobación');
      setAprobadorDialogOpen(false);
      fetchResolucion();
      fetchHistorial(true);
    } catch (err) {
      console.error('Error enviando a aprobación:', err);
      toast.error(err.response?.data?.detail || 'Error al enviar a aprobación');
    } finally {
      setSaving(false);
    }
  };

  const handleAprobar = async () => {
    setAprobando(true);
    setSaving(true);
    try {
      const response = await api.post(`/reclamos/${reclamoId}/resolucion/aprobar`);
      toast.success('Resolución aprobada');
      if (response.data.envio_automatico) {
        toast.info('El correo al consumidor se ha enviado automáticamente');
      }
      await fetchResolucion();
      await fetchHistorial(true);
    } catch (err) {
      console.error('Error aprobando:', err);
      toast.error(err.response?.data?.detail || 'Error al aprobar');
    } finally {
      setSaving(false);
      setAprobando(false);
    }
  };

  const handlePosponer = async () => {
    if (motivoPosposicion.trim().length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres');
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('motivo', motivoPosposicion);
      formData.append('anotaciones', JSON.stringify(anotaciones));
      archivosPosponer.forEach(file => {
        formData.append('archivos', file);
      });
      
      await api.post(`/reclamos/${reclamoId}/resolucion/posponer`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Resolución pospuesta');
      setPosponerDialogOpen(false);
      setMotivoPosposicion('');
      setArchivosPosponer([]);
      setAnotaciones([]);
      fetchResolucion();
      fetchHistorial(true);
    } catch (err) {
      console.error('Error posponiendo:', err);
      toast.error(err.response?.data?.detail || 'Error al posponer');
    } finally {
      setSaving(false);
    }
  };

  const handleEnviarCorreo = async () => {
    setEnviandoCorreo(true);
    try {
      const response = await api.post(`/reclamos/${reclamoId}/resolucion/enviar-correo`);
      toast.success(`Correo enviado a ${response.data.enviado_a}`);
      fetchResolucion();
      fetchHistorial(true);
    } catch (err) {
      console.error('Error enviando correo:', err);
      toast.error(err.response?.data?.detail || 'Error al enviar correo');
    } finally {
      setEnviandoCorreo(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const config = ESTADOS_RESOLUCION[estado] || { label: estado, color: 'bg-gray-100 text-gray-800', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const ALLOWED_EXTENSIONS = '.doc,.docx,.pdf,.jpg,.png,.jpeg,.mov,.mp4';
  
  const handleAddFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const total = archivosPosponer.length + files.length;
    if (total > 5) {
      toast.error('Máximo 5 archivos permitidos');
      return;
    }
    setArchivosPosponer(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setArchivosPosponer(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (tipo) => {
    if (['jpg', 'jpeg', 'png'].includes(tipo)) return FileImage;
    if (['mov', 'mp4'].includes(tipo)) return FileVideo;
    if (['doc', 'docx'].includes(tipo)) return FileText;
    if (tipo === 'pdf') return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canPreview = (tipo) => ['jpg', 'jpeg', 'png', 'pdf', 'mp4', 'mov'].includes(tipo);

  const handlePreview = async (historialId, indice, archivo) => {
    try {
      const response = await api.get(
        `/resoluciones/historial/${historialId}/archivo/${indice}?preview=true`,
        { responseType: 'blob' }
      );
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      setPreviewModal({ open: true, url: blobUrl, tipo: archivo.tipo, nombre: archivo.nombre });
    } catch {
      toast.error('Error al previsualizar el archivo');
    }
  };

  const handleDownloadHistorialFile = async (historialId, indice, nombre) => {
    try {
      const response = await api.get(
        `/resoluciones/historial/${historialId}/archivo/${indice}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al descargar el archivo');
    }
  };

  const puedeEditar = !esAprobadorAsignado && !esLegal && (!resolucion || resolucion?.estado === 'EN_PROCESO' || resolucion?.estado === 'POSPUESTO');
  const puedeEnviarAprobacion = resolucion && (resolucion.estado === 'EN_PROCESO' || resolucion.estado === 'POSPUESTO');
  const puedeAprobarPosponer = esAprobadorAsignado && resolucion?.estado === 'PENDIENTE_APROBACION';
  const puedeEnviarCorreo = esAdmin && resolucion?.estado === 'APROBADO' && !resolucion?.correo_enviado_consumidor;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!reclamo) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Reclamo no encontrado</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="resolucion-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Resolución del Reclamo
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Folio: <Link 
              to={`/admin/reclamos?detail=${reclamoId}`} 
              className="font-mono font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
              data-testid="folio-link"
            >{reclamo.folio}</Link>
          </p>
        </div>
        {resolucion && getEstadoBadge(resolucion.estado)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna Principal */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {/* Datos del Reclamo */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-500" />
                Datos del Reclamo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Reclamante:</span>
                  <p className="font-medium">{reclamo.nombre_completo}</p>
                </div>
                <div>
                  <span className="text-slate-500">Correo:</span>
                  <p className="font-medium">{reclamo.correo_electronico}</p>
                </div>
                <div>
                  <span className="text-slate-500">Fecha:</span>
                  <p className="font-medium">
                    {format(new Date(reclamo.created_at), "dd 'de' MMMM 'de' yyyy", { locale: es })}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">Proyecto:</span>
                  <p className="font-medium">{reclamo.proyectos?.nombre}</p>
                </div>
              </div>
              <div className="pt-3 border-t">
                <span className="text-slate-500 text-sm">Descripción del reclamo:</span>
                <p className="mt-1 text-sm bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">
                  {reclamo.descripcion_reclamo}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Editor de Resolución */}
          <div className="relative">
          {aprobando && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg" data-testid="aprobando-overlay">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border">
                <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                <span className="text-sm font-medium text-slate-600">Aprobando resolución...</span>
              </div>
            </div>
          )}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Scale className="w-4 h-4 text-slate-500" />
                Contenido de la Resolución
                {resolucion?.version > 1 && (
                  <Badge variant="outline" className="ml-2">Versión {resolucion.version}</Badge>
                )}
                {puedeEditar && iaHabilitado && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-8 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                    onClick={async () => {
                      if (contenido && contenido.replace(/<[^>]*>/g, '').trim()) {
                        if (!window.confirm('Esto reemplazará el contenido actual con una resolución generada por IA. ¿Desea continuar?')) return;
                      }
                      setGenerandoIA(true);
                      try {
                        const res = await api.post(`/reclamos/${reclamoId}/generar-resolucion-ia`);
                        setContenido(res.data.html);
                        toast.success('Resolución generada con IA exitosamente');
                        fetchHistorial(true);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Error al generar resolución con IA');
                      } finally {
                        setGenerandoIA(false);
                      }
                    }}
                    disabled={generandoIA}
                    data-testid="btn-generar-ia"
                  >
                    {generandoIA ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                    {generandoIA ? 'Generando...' : 'Generar con IA'}
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {puedeEditar || !resolucion ? (
                <>
                  <ReactQuill
                    theme="snow"
                    value={contenido}
                    onChange={setContenido}
                    modules={quillModules}
                    placeholder="Escriba aquí el contenido de la resolución formal..."
                    className="bg-white rounded-lg"
                    data-testid="contenido-resolucion"
                    style={{ minHeight: '250px' }}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={handleGuardar}
                      disabled={saving}
                      data-testid="btn-guardar"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Guardar Borrador
                    </Button>
                    {puedeEnviarAprobacion && (
                      <Button
                        onClick={handleAbrirSelectorAprobador}
                        disabled={saving}
                        className="text-white"
                        data-testid="btn-enviar-aprobacion"
                        {...getPrimaryButtonStyles(themeColors)}
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        Enviar a aprobación
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Modo anotaciones para el aprobador asignado */}
                  {puedeAprobarPosponer ? (
                    <div className="bg-slate-50 rounded-lg p-4 border overflow-hidden">
                      <AnnotatedContent
                        html={resolucion?.contenido_resolucion || ''}
                        annotations={anotaciones}
                        readOnly={false}
                        onAdd={(ann) => setAnotaciones(prev => [...prev, ann])}
                        onRemove={(id) => setAnotaciones(prev => prev.filter(a => a.id !== id))}
                      />
                    </div>
                  ) : (
                    <ReactQuill
                      theme="snow"
                      value={resolucion?.contenido_resolucion || ''}
                      readOnly={true}
                      modules={{ toolbar: false }}
                      className="bg-slate-50 rounded-lg [&_.ql-container]:border-0 [&_.ql-editor]:min-h-0"
                    />
                  )}
                  {(resolucion?.estado === 'APROBADO' || (puedeAprobarPosponer)) && (
                    <div className="flex flex-wrap justify-end gap-2 pt-2">
                      <Button
                        variant="outline"
                        disabled={downloadingPdf}
                        onClick={async () => {
                          try {
                            const response = await api.get(
                              `/reclamos/${reclamoId}/resolucion/descargar-word`,
                              { responseType: 'blob' }
                            );
                            const disposition = response.headers['content-disposition'] || '';
                            const match = disposition.match(/filename="?(.+?)"?$/);
                            const filename = match ? match[1] : `Resolucion-${reclamoId}.docx`;
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (err) {
                            toast.error('Error al descargar el documento');
                          }
                        }}
                        data-testid="btn-descargar-word"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Descargar Word
                      </Button>
                      {resolucion?.estado === 'APROBADO' && (
                        <>
                          <Button
                            variant="outline"
                            disabled={downloadingPdf}
                            onClick={async () => {
                              setDownloadingPdf(true);
                              try {
                                const response = await api.get(
                                  `/reclamos/${reclamoId}/resolucion/descargar-pdf`,
                                  { responseType: 'blob' }
                                );
                                const disposition = response.headers['content-disposition'] || '';
                                const match = disposition.match(/filename="?(.+?)"?$/);
                                const filename = match ? match[1] : `Resolucion-${reclamoId}.pdf`;
                                const contentType = response.headers['content-type'] || 'application/pdf';
                                const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', filename);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                              } catch (err) {
                                toast.error('Error al descargar el PDF');
                              } finally {
                                setDownloadingPdf(false);
                              }
                            }}
                            data-testid="btn-descargar-pdf"
                          >
                            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
                            Descargar PDF
                          </Button>
                          {/* Ocultar si reclamo está Terminado, si PDF ya adjuntado, o si usuario es LEGAL */}
                          {reclamo.estado !== 'TERMINADO' && !resolucion?.pdf_ya_adjuntado && user?.rol !== 'LEGAL' && (
                            <Button
                              variant="outline"
                              disabled={adjuntandoPdf}
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              onClick={async () => {
                                setAdjuntandoPdf(true);
                                try {
                                  await api.post(`/reclamos/${reclamoId}/resolucion/adjuntar-pdf`);
                                  toast.success('PDF de resolución adjuntado al reclamo exitosamente');
                                  fetchResolucion();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Error al adjuntar el PDF');
                                } finally {
                                  setAdjuntandoPdf(false);
                                }
                              }}
                              data-testid="btn-adjuntar-pdf"
                            >
                              {adjuntandoPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                              Adjuntar a reclamo
                            </Button>
                          )}
                          {resolucion?.pdf_ya_adjuntado && (
                            <span className="text-xs text-green-600 flex items-center gap-1" data-testid="pdf-ya-adjuntado-label">
                              <CheckCircle className="w-3.5 h-3.5" />
                              PDF ya adjuntado
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Info del aprobador asignado */}
              {resolucion?.aprobador_asignado && resolucion?.estado === 'PENDIENTE_APROBACION' && (
                <Alert className="border-blue-200 bg-blue-50">
                  <UserCheck className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <strong>Aprobador asignado:</strong> {resolucion.aprobador_asignado.nombre} ({resolucion.aprobador_asignado.correo})
                  </AlertDescription>
                </Alert>
              )}

              {/* Motivo de postergación */}
              {resolucion?.estado === 'POSPUESTO' && resolucion?.motivo_posposicion && (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    <strong>Motivo de postergación:</strong> {resolucion.motivo_posposicion}
                  </AlertDescription>
                </Alert>
              )}

              {/* Botones para Aprobador Asignado */}
              {puedeAprobarPosponer && (
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setPosponerDialogOpen(true)}
                    disabled={saving}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="btn-posponer"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Posponer
                  </Button>
                  <Button
                    onClick={handleAprobar}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="btn-aprobar"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Aprobar Resolución
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          </div>

          {/* Sección de Notificación al Consumidor - Dinámica */}
          {resolucion && (
            <Card className="border-slate-200 !mt-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  Notificación al Consumidor
                </CardTitle>
              </CardHeader>
              <CardContent>
                {resolucion.estado_notificacion_consumidor === 'ENVIADO' ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="notif-enviado">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-800">Correo enviado correctamente</p>
                        {resolucion.detalle_notificacion?.via === 'respuesta_formal' && (
                          <p className="text-sm text-green-700 mt-1">
                            Enviado vía respuesta formal al reclamante
                          </p>
                        )}
                        {resolucion.detalle_notificacion?.fecha && (
                          <p className="text-sm text-green-700 mt-1">
                            Enviado el {format(new Date(resolucion.detalle_notificacion.fecha), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}
                          </p>
                        )}
                        {resolucion.detalle_notificacion?.enviado_por && (
                          <p className="text-sm text-green-700">
                            Por: {resolucion.detalle_notificacion.enviado_por}
                          </p>
                        )}
                        <p className="text-sm text-green-700">
                          Destinatario: {reclamo.correo_electronico}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : resolucion.estado_notificacion_consumidor === 'PENDIENTE' ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="notif-pendiente">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">Correo pendiente de envío</p>
                          <p className="text-sm text-amber-700 mt-1">
                            La resolución fue aprobada pero el correo aún no ha sido enviado al consumidor.
                          </p>
                          <p className="text-sm text-amber-700">
                            Destinatario: {reclamo.correo_electronico}
                          </p>
                        </div>
                      </div>
                      {puedeEnviarCorreo && (
                        <Button
                          onClick={handleEnviarCorreo}
                          disabled={enviandoCorreo}
                          className="text-white flex-shrink-0"
                          data-testid="btn-enviar-correo"
                          {...getPrimaryButtonStyles(themeColors)}
                        >
                          {enviandoCorreo ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Mail className="w-4 h-4 mr-2" />
                          )}
                          Enviar Correo
                        </Button>
                      )}
                    </div>
                  </div>
                ) : resolucion.estado_notificacion_consumidor === 'PENDIENTE_APROBACION' ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="notif-pendiente-aprobacion">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-800">Pendiente de aprobación</p>
                        <p className="text-sm text-blue-700 mt-1">
                          La resolución está en revisión. El correo se podrá enviar una vez aprobada.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : resolucion.estado_notificacion_consumidor === 'EN_PROCESO' ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="notif-en-proceso">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-700">Resolución en proceso</p>
                        <p className="text-sm text-slate-600 mt-1">
                          La resolución aún está siendo elaborada. El correo se enviará después de la aprobación.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Columna Lateral - Historial */}
        <div className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" />
                  Historial
                  {refreshingHistorial && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchHistorial}
                  disabled={loadingHistorial || !resolucion}
                  title="Actualizar historial"
                >
                  {loadingHistorial ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingHistorial ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : historial.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  {resolucion ? 'No hay registros en el historial' : 'No hay historial disponible'}
                </p>
              ) : (
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {historial.map((item, index) => (
                    <div key={item.id} className="relative pl-6 pb-4 last:pb-0">
                      {/* Línea vertical */}
                      {index < historial.length - 1 && (
                        <div className="absolute left-2 top-3 bottom-0 w-px bg-slate-200" />
                      )}
                      {/* Punto */}
                      <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-slate-200 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-slate-400" />
                      </div>
                      {/* Contenido */}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {getEstadoBadge(item.estado_nuevo)}
                        </div>
                        <p className="text-xs text-slate-500">
                          {format(new Date(item.created_at), "dd/MM/yyyy HH:mm", { locale: es })}
                        </p>
                        {item.usuarios?.nombre && (
                          <p className="text-xs text-slate-500">Por: {item.usuarios.nombre}</p>
                        )}
                        {item.motivo && (
                          <p className="text-sm text-slate-600 mt-1 bg-slate-50 p-2 rounded">
                            {item.motivo}
                          </p>
                        )}
                        {/* Archivos adjuntos */}
                        {item.archivos_adjuntos && item.archivos_adjuntos.length > 0 && (
                          <div className="mt-2 space-y-1" data-testid={`historial-archivos-${item.id}`}>
                            <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                              <Paperclip className="w-3 h-3" />
                              {item.archivos_adjuntos.length} archivo(s) adjunto(s)
                            </p>
                            {item.archivos_adjuntos.map((archivo, idx) => {
                              const IconComp = getFileIcon(archivo.tipo);
                              const previewable = canPreview(archivo.tipo);
                              return (
                                <div key={idx} className="flex items-center gap-1.5 text-xs bg-slate-50 rounded p-1.5 group">
                                  <IconComp className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                  <span className="truncate flex-1 text-slate-600" title={archivo.nombre}>
                                    {archivo.nombre}
                                  </span>
                                  <span className="text-slate-400 flex-shrink-0">
                                    {formatFileSize(archivo.tamaño)}
                                  </span>
                                  {previewable && (
                                    <button
                                      onClick={() => handlePreview(item.id, idx, archivo)}
                                      className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                                      title="Ver"
                                      data-testid={`preview-archivo-${item.id}-${idx}`}
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDownloadHistorialFile(item.id, idx, archivo.nombre)}
                                    className="text-slate-500 hover:text-slate-700 flex-shrink-0"
                                    title="Descargar"
                                    data-testid={`download-archivo-${item.id}-${idx}`}
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Anotaciones inline del historial */}
                        {item.anotaciones && item.anotaciones.length > 0 && (
                          <div className="mt-2 space-y-1" data-testid={`historial-anotaciones-${item.id}`}>
                            <p className="text-xs font-medium text-blue-600 flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {item.anotaciones.length} anotación(es) inline
                            </p>
                            <div className="bg-blue-50/50 rounded p-2 space-y-1.5">
                              {item.anotaciones.map((ann, idx) => (
                                <div key={ann.id || idx} className="text-xs border-l-2 border-blue-300 pl-2">
                                  <p className="text-slate-500 italic truncate">"{ann.texto}"</p>
                                  <p className="text-slate-700">{ann.comentario}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={historialEndRef} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info del estado actual */}
          {resolucion && (
            <Card className="border-slate-200 !mt-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Información</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <span className="text-slate-500">Creado por:</span>
                  <p className="font-medium">{resolucion.usuarios?.nombre || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Fecha de creación:</span>
                  <p className="font-medium">
                    {format(new Date(resolucion.created_at), "dd/MM/yyyy HH:mm", { locale: es })}
                  </p>
                </div>
                {resolucion.fecha_aprobacion && (
                  <div>
                    <span className="text-slate-500">Fecha de aprobación:</span>
                    <p className="font-medium">
                      {format(new Date(resolucion.fecha_aprobacion), "dd/MM/yyyy HH:mm", { locale: es })}
                    </p>
                  </div>
                )}
                {resolucion.aprobador?.nombre && (
                  <div>
                    <span className="text-slate-500">Aprobado por:</span>
                    <p className="font-medium">{resolucion.aprobador.nombre}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Dialog para Posponer */}
      <Dialog open={posponerDialogOpen} onOpenChange={(open) => {
        if (!open) { setArchivosPosponer([]); setMotivoPosposicion(''); }
        setPosponerDialogOpen(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-amber-500" />
              Posponer Resolución
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-hidden">
            <p className="text-sm text-slate-600">
              Indique el motivo por el cual la resolución debe ser revisada nuevamente.
            </p>
            <Textarea
              value={motivoPosposicion}
              onChange={(e) => setMotivoPosposicion(e.target.value)}
              placeholder="Escriba el motivo de la postergación (mínimo 10 caracteres)..."
              rows={4}
              data-testid="motivo-posposicion"
            />
            
            {/* Resumen de anotaciones si existen */}
            {anotaciones.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="anotaciones-resumen-posponer">
                <p className="text-xs font-semibold text-blue-700 flex items-center gap-1.5 mb-1.5">
                  <MessageSquare className="w-3.5 h-3.5" />
                  {anotaciones.length} anotación(es) inline se enviarán con esta postergación
                </p>
                {anotaciones.map((ann, idx) => (
                  <p key={ann.id} className="text-xs text-blue-600 truncate">
                    {idx + 1}. "{ann.texto.slice(0, 40)}..." → {ann.comentario.slice(0, 50)}{ann.comentario.length > 50 ? '...' : ''}
                  </p>
                ))}
              </div>
            )}

            {/* Zona de archivos adjuntos */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" />
                Archivos adjuntos (opcional, máx. 5)
              </Label>
              <p className="text-xs text-slate-500">
                Formatos: .doc, .docx, .pdf, .jpg, .png, .jpeg, .mov, .mp4
              </p>
              
              {archivosPosponer.length < 5 && (
                <label
                  className="flex items-center justify-center w-full h-20 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors"
                  data-testid="upload-archivos-posponer"
                >
                  <div className="text-center">
                    <Paperclip className="w-5 h-5 mx-auto text-slate-400 mb-1" />
                    <span className="text-xs text-slate-500">Haga clic para seleccionar archivos</span>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept={ALLOWED_EXTENSIONS}
                    onChange={handleAddFiles}
                    className="hidden"
                  />
                </label>
              )}
              
              {archivosPosponer.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-slate-500 font-medium">{archivosPosponer.length}/5 archivos</p>
                  {archivosPosponer.map((file, index) => {
                    const ext = file.name.split('.').pop()?.toLowerCase() || '';
                    const IconComp = getFileIcon(ext);
                    return (
                      <div key={index} className="flex items-center gap-2 bg-slate-50 rounded p-2 text-sm min-w-0 overflow-hidden" data-testid={`archivo-posponer-${index}`}>
                        <IconComp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate flex-1 min-w-0">{file.name}</span>
                        <span className="text-xs text-slate-400 flex-shrink-0">{formatFileSize(file.size)}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-400 hover:text-red-600 flex-shrink-0"
                          data-testid={`remove-archivo-${index}`}
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPosponerDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handlePosponer}
              disabled={saving || motivoPosposicion.trim().length < 10}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Posponer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para Seleccionar Aprobador */}
      <Dialog open={aprobadorDialogOpen} onOpenChange={(open) => {
        if (!open) setAprobadorPopoverOpen(false);
        setAprobadorDialogOpen(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-blue-500" />
              Seleccionar Aprobador
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Seleccione el usuario con rol Legal que será responsable de aprobar esta resolución.
            </p>
            
            {loadingUsuariosLegales ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="aprobador">Aprobador *</Label>
                <Popover open={aprobadorPopoverOpen} onOpenChange={setAprobadorPopoverOpen} modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={aprobadorPopoverOpen}
                      className="w-full justify-between font-normal"
                      data-testid="select-aprobador"
                    >
                      {aprobadorSeleccionado
                        ? (() => {
                            const u = usuariosLegales.find(u => u.id === aprobadorSeleccionado);
                            return u ? `${u.nombre} (${u.rol})` : 'Seleccione un aprobador';
                          })()
                        : 'Seleccione un aprobador'}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar por nombre, correo o rol..." />
                      <CommandList>
                        <CommandEmpty>No se encontraron usuarios.</CommandEmpty>
                        <CommandGroup>
                          {usuariosLegales.map((usuario) => (
                            <CommandItem
                              key={usuario.id}
                              value={`${usuario.nombre} ${usuario.correo} ${usuario.rol}`}
                              onSelect={() => {
                                setAprobadorSeleccionado(usuario.id);
                                setAprobadorPopoverOpen(false);
                              }}
                              className="data-[selected=true]:!bg-[hsl(var(--theme-primary-hsl)/0.20)] data-[selected=true]:!text-slate-900"
                              data-testid={`aprobador-option-${usuario.id}`}
                            >
                              <div className="flex flex-col">
                                <span>{usuario.nombre} <span className="text-xs text-slate-400">({usuario.rol})</span></span>
                                <span className="text-xs text-slate-500">{usuario.correo}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAprobadorDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleEnviarAprobacion}
              disabled={saving || !aprobadorSeleccionado}
              className="text-white"
              {...getPrimaryButtonStyles(themeColors)}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar a Aprobación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Previsualización */}
      <Dialog open={previewModal.open} onOpenChange={(open) => {
        if (!open && previewModal.url) {
          window.URL.revokeObjectURL(previewModal.url);
        }
        setPreviewModal(prev => ({ ...prev, open }));
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Eye className="w-4 h-4" />
              {previewModal.nombre}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[300px] max-h-[70vh] overflow-auto">
            {['jpg', 'jpeg', 'png'].includes(previewModal.tipo) && (
              <img
                src={previewModal.url}
                alt={previewModal.nombre}
                className="max-w-full max-h-[65vh] object-contain rounded"
                data-testid="preview-image"
              />
            )}
            {previewModal.tipo === 'pdf' && (
              <iframe
                src={previewModal.url}
                title={previewModal.nombre}
                className="w-full h-[65vh] rounded border"
                data-testid="preview-pdf"
              />
            )}
            {['mp4', 'mov'].includes(previewModal.tipo) && (
              <video
                src={previewModal.url}
                controls
                className="max-w-full max-h-[65vh] rounded"
                data-testid="preview-video"
              >
                Tu navegador no soporta la reproducción de video.
              </video>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResolucionPage;
